import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Box,
  Divider,
  Paper,
  CircularProgress,
} from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';
import billService, { Bill } from '../services/billService';

interface ViewBillDialogProps {
  open: boolean;
  onClose: () => void;
  bill: Bill;
}

const ViewBillDialog: React.FC<ViewBillDialogProps> = ({
  open,
  onClose,
  bill,
}) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadInvoice = async () => {
    try {
      setDownloading(true);
      await billService.downloadInvoice(bill.id, bill.bill_number);
    } finally {
      setDownloading(false);
    }
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID');
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Belum Bayar';
      case 'paid':
        return 'Sudah Bayar';
      case 'overdue':
        return 'Terlambat';
      case 'cancelled':
        return 'Dibatalkan';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Detail Tagihan</Typography>
          <Chip
            label={getStatusLabel(bill.status)}
            color={getStatusColor(bill.status)}
            size="small"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Bill Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Informasi Tagihan
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <Typography variant="body2" color="textSecondary">
                  Nomor Tagihan
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {bill.bill_number}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <Typography variant="body2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={getStatusLabel(bill.status)}
                  color={getStatusColor(bill.status)}
                  size="small"
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <Typography variant="body2" color="textSecondary">
                  Periode Tagihan
                </Typography>
                <Typography variant="body1">
                  {formatDate(bill.billing_period_start)} - {formatDate(bill.billing_period_end)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <Typography variant="body2" color="textSecondary">
                  Jatuh Tempo
                </Typography>
                <Typography variant="body1">
                  {formatDate(bill.due_date)}
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 100%' }}>
                <Typography variant="body2" color="textSecondary">
                  Dibuat pada
                </Typography>
                <Typography variant="body1">
                  {formatDate(bill.created_at)}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {/* Customer Information */}
            <Box sx={{ flex: 1, minWidth: '300px' }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Informasi Customer
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Nama
                    </Typography>
                    <Typography variant="body1">
                      {bill.customer?.name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Kode Customer
                    </Typography>
                    <Typography variant="body1">
                      {bill.customer?.customer_code}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Username
                    </Typography>
                    <Typography variant="body1">
                      {bill.customer?.username}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {bill.customer?.email || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Telepon
                    </Typography>
                    <Typography variant="body1">
                      {bill.customer?.phone || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* Package Information */}
            <Box sx={{ flex: 1, minWidth: '300px' }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Informasi Paket
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Nama Paket
                    </Typography>
                    <Typography variant="body1">
                      {bill.package?.name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Bandwidth
                    </Typography>
                    <Typography variant="body1">
                      {bill.package?.bandwidth_download} / {bill.package?.bandwidth_upload}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Harga Paket
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(bill.package?.price || 0)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* Payment Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Rincian Pembayaran
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: '250px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">Jumlah Tagihan:</Typography>
                  <Typography variant="body1">{formatCurrency(bill.amount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">Pajak:</Typography>
                  <Typography variant="body1">{formatCurrency(bill.tax_amount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">Diskon:</Typography>
                  <Typography variant="body1">-{formatCurrency(bill.discount_amount)}</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="h6" fontWeight="bold">Total:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {formatCurrency(bill.total_amount)}
                  </Typography>
                </Box>
              </Box>

              {bill.status === 'paid' && (
                <Box sx={{ flex: 1, minWidth: '250px' }}>
                  <Typography variant="body2" color="textSecondary">
                    Tanggal Pembayaran
                  </Typography>
                  <Typography variant="body1">
                    {bill.payment_date ? formatDate(bill.payment_date) : '-'}
                  </Typography>
                  
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Metode Pembayaran
                  </Typography>
                  <Typography variant="body1">
                    {bill.payment_method || '-'}
                  </Typography>
                  
                  {bill.notes && (
                    <>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Catatan Pembayaran
                      </Typography>
                      <Typography variant="body1">
                        {bill.notes}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Paper>

          {bill.notes && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Catatan
              </Typography>
              <Typography variant="body1">
                {bill.notes}
              </Typography>
            </Paper>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button
          variant="outlined"
          startIcon={downloading ? <CircularProgress size={16} /> : <PdfIcon />}
          onClick={handleDownloadInvoice}
          disabled={downloading}
        >
          Unduh Invoice PDF
        </Button>
        <Button onClick={onClose}>Tutup</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewBillDialog;
